import React, { useMemo } from 'react';
import { IEventAudit, IEvent } from 'model/v2';
import moment from 'moment';
import momentTz from 'moment-timezone';
import { AuditViewWrapper } from './style';
import { ICancellationReason } from 'model';
import Skeleton from '@material-ui/lab/Skeleton';

/**
 * The "before" value of a direct in-place edit, shown struck-through in History:
 * the OLD start–end time for a time edit, otherwise the OLD provider /
 * appointment-type name. Returns null for audit rows with no direct-edit detail
 * (creates, cancels, manual edits, …).
 */
const renderPreviousValue = (
  audit: IEventAudit,
  fallbackTimezone?: string
): React.ReactNode => {
  const changed = audit.directEditChanges?.changed;
  if (!changed) return null;

  // Struck-through, but the SAME color as the rest of the History text
  // (inherit) — only the line-through distinguishes the old value.
  const strike: React.CSSProperties = {
    textDecoration: 'line-through'
  };

  // Time edit → old start–end time in the event's timezone.
  if (changed.startDate?.from && changed.endDate?.from) {
    const tz =
      audit.directEditChanges?.timezone || fallbackTimezone || momentTz.tz.guess();
    const from = momentTz.tz(changed.startDate.from, tz);
    const to = momentTz.tz(changed.endDate.from, tz);
    // Start and end are always on the same day, so a single date is enough.
    return (
      <>
        <p style={strike}>{from.format('MM/DD/YY')}</p>
        <p style={strike}>
          {from.format('hh:mm a')} - {to.format('hh:mm a')} {tz}
        </p>
      </>
    );
  }

  // Provider / appointment-type change → old name.
  const oldName =
    changed.provider?.fromLabel ?? changed.appointmentType?.fromLabel;
  if (oldName) {
    return <p style={strike}>{oldName}</p>;
  }
  return null;
};

interface Props {
  event: IEvent;
  cancellationReasons?: ICancellationReason[];
  isObservation?: boolean;
  isLoading?: boolean;
}

const AuditView: React.FC<Props> = ({
  event,
  cancellationReasons,
  isObservation,
  isLoading
}: Props) => {
  const cancellationReasonsMap = useMemo(() => {
    const map = new Map();
    cancellationReasons?.forEach(c => {
      map.set(c.reasonId, c);
    });
    return map;
  }, [cancellationReasons]);

  return (
    <AuditViewWrapper isObservation={isObservation}>
      <h2 className="history-title">History</h2>

      {!isLoading ? (
        event?.eventAudit ? (
          [...event?.eventAudit]
            .sort((a, b) => moment(b.actionAt).diff(a.actionAt))
            .map((audit: IEventAudit) => (
              <div key={audit.id} className="history-content">
                {
                  <div>
                    <p>
                      {audit.action === 'Delete' ? 'Cancel' : audit.action} by{' '}
                      {audit.actionBy}
                    </p>
                    <p>{moment(audit.actionAt).format('MM/DD/YY, LT')} </p>
                    {renderPreviousValue(audit, event?.clinic?.timezone)}
                    {audit.reason && (
                      <p>{cancellationReasonsMap.get(audit.reason)?.name}</p>
                    )}
                    {audit.note && <p>{audit.note}</p>}
                  </div>
                }
              </div>
            ))
        ) : (
          <span>N/A</span>
        )
      ) : (
        <Skeleton
          variant="text"
          children={
            <div>
              <p>{'Deleted by user'} </p>
              <p>{'MM/DD/YY, LT'} </p>
            </div>
          }
        />
      )}
    </AuditViewWrapper>
  );
};
export default React.memo(AuditView);
